import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  Wrench,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { getAllTeamMembers } from '../services/teamService';
import { getAllTools, createTool, updateTool } from '../services/toolsInventoryService';
import './ToolsInventory.css';
import { useLotScope } from '../hooks/useLotScope';

const ITEMS_PER_PAGE = 10;

const LOT_TAB_OPTIONS = [
  { value: 'all', label: 'All LOTs' },
  { value: 'LOT 8 (Morogoro)', label: 'LOT 8' },
  { value: 'LOT 10 (Manyara)', label: 'LOT 10' },
];

const STATUS_LABELS = {
  in_use: 'In use',
  available: 'Available',
  maintenance: 'Maintenance',
};

const emptyForm = {
  lot: '',
  name: '',
  model: '',
  serialNumber: '',
  user: '',
  dateAssigned: '',
  estimatedValue: '',
  status: 'in_use',
};

function parseDate(dateString) {
  if (!dateString) return null;
  const parts = String(dateString).split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  return new Date(year, month, day);
}

function isDateInRange(dateString, startDate, endDate) {
  if (!dateString) return true;
  const date = parseDate(dateString);
  if (!date) return true;

  if (startDate) {
    const start = parseDate(startDate);
    if (start && date < start) return false;
  }

  if (endDate) {
    const end = parseDate(endDate);
    if (end) {
      end.setHours(23, 59, 59, 999);
      if (date > end) return false;
    }
  }

  return true;
}

function parseCurrency(currencyString) {
  if (!currencyString || currencyString === '-') return 0;
  const cleaned = String(currencyString).replace(/TZS\s*/gi, '').replace(/,/g, '').trim();
  return parseFloat(cleaned) || 0;
}

function formatCurrency(amount) {
  return `TZS ${Number(amount || 0).toLocaleString('en-US')}`;
}

function statusLabel(status) {
  return STATUS_LABELS[status] || status || '—';
}

function TiPageSkeleton() {
  return (
    <div className="ti-skeleton" aria-busy="true" aria-label="Loading tools inventory">
      <section className="ti-intro" aria-hidden>
        <div className="ti-intro-text">
          <Skeleton className="h-7 w-52 max-w-full" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-full" />
        </div>
        <div className="ti-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[8.5rem] rounded-lg" />
        </div>
      </section>
      <div className="ti-lot-tabs" aria-hidden>
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
      </div>
      <div className="ti-summary-cards" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="ti-summary-card ti-summary-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-20" />
            <Skeleton className="mt-4 h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="ti-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      <div className="ti-table-shell" aria-hidden>
        <div className="ti-skel-rows">
          {Array.from({ length: 6 }, (_, r) => (
            <Skeleton key={r} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ToolsInventory() {
  const [searchQuery, setSearchQuery] = useState('');
  const { lotFilter, setLotFilter, lockedLotFilter, isReadOnly } = useLotScope('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingToolId, setEditingToolId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const members = await getAllTeamMembers();
        if (!cancelled) setTeamMembers(members || []);
      } catch (err) {
        console.error('Error loading team members:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const toolsData = await getAllTools();
        if (!cancelled) setTools(toolsData || []);
      } catch (err) {
        console.error('Error loading tools:', err);
        if (!cancelled) {
          setTools([]);
          setError('Failed to load tools. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearAllFilters = () => {
    setSearchQuery('');
    setLotFilter('all');
    setStatusFilter('all');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    lotFilter !== 'all' ||
    statusFilter !== 'all' ||
    Boolean(startDate) ||
    Boolean(endDate);

  const filteredTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tools.filter((tool) => {
      if (lotFilter !== 'all' && tool.lot && tool.lot !== lotFilter) return false;
      if (statusFilter !== 'all' && tool.status !== statusFilter) return false;
      if (!isDateInRange(tool.dateAssigned, startDate, endDate)) return false;
      if (!query) return true;
      const searchableText = [
        tool.lot,
        tool.name,
        tool.model,
        tool.serialNumber,
        tool.user,
        tool.dateAssigned,
        tool.estimatedValue,
        statusLabel(tool.status),
      ]
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [tools, lotFilter, statusFilter, startDate, endDate, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [lotFilter, statusFilter, startDate, endDate, searchQuery]);

  const totalFiltered = filteredTools.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedTools = useMemo(
    () => filteredTools.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [filteredTools, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.ti-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const lotCounts = useMemo(() => {
    const counts = { all: tools.length };
    LOT_TAB_OPTIONS.forEach((opt) => {
      if (opt.value === 'all') return;
      counts[opt.value] = tools.filter((t) => t.lot === opt.value).length;
    });
    return counts;
  }, [tools]);

  const stats = useMemo(() => {
    const total = tools.length;
    const inUse = tools.filter((t) => t.status === 'in_use').length;
    const available = tools.filter((t) => t.status === 'available').length;
    const maintenance = tools.filter((t) => t.status === 'maintenance').length;
    return {
      total,
      inUse,
      available,
      maintenance,
      inUsePct: total > 0 ? Math.round((inUse / total) * 100) : 0,
      availablePct: total > 0 ? Math.round((available / total) * 100) : 0,
      maintenancePct: total > 0 ? Math.round((maintenance / total) * 100) : 0,
    };
  }, [tools]);

  const subtotalValue = useMemo(() => {
    const total = filteredTools.reduce((sum, tool) => sum + parseCurrency(tool.estimatedValue), 0);
    return formatCurrency(total);
  }, [filteredTools]);

  const handleExportToExcel = () => {
    try {
      if (tools.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = tools.map((tool) => ({
        'LOT Name': tool.lot || '',
        'Tool Name': tool.name || '',
        Model: tool.model || '',
        'Serial Number': tool.serialNumber || '',
        'User Name': tool.user || '',
        'Date Assigned': tool.dateAssigned || '',
        'Estimated Value': tool.estimatedValue || '',
        Status: statusLabel(tool.status),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tools Inventory');
      ws['!cols'] = [
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
      ];

      XLSX.writeFile(wb, `Tools_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setEditingToolId(null);
    setFormError('');
    setShowModal(false);
  };

  const openCreateModal = () => {
    setEditingToolId(null);
    setFormError('');
    setFormData({ ...emptyForm });
    setShowModal(true);
  };

  const handleEditClick = (tool) => {
    setEditingToolId(tool.id);
    setFormError('');
    setFormData({
      lot: tool.lot || '',
      name: tool.name || '',
      model: tool.model || '',
      serialNumber: tool.serialNumber || '',
      user: tool.user || '',
      dateAssigned: tool.dateAssigned || '',
      estimatedValue: tool.estimatedValue || '',
      status: tool.status || 'available',
    });
    setShowModal(true);
  };

  const handleRecordTool = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');

    try {
      if (editingToolId) {
        await updateTool(editingToolId, formData);
      } else {
        await createTool(formData);
      }
      const toolsData = await getAllTools();
      setTools(toolsData || []);
      setFormData({ ...emptyForm });
      setEditingToolId(null);
      setShowModal(false);
    } catch (err) {
      console.error('Error saving tool:', err);
      setFormError('Failed to save tool. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const summaryCards = [
    {
      id: 'total',
      label: 'Total tools',
      value: stats.total,
      accent: '#3b82f6',
      icon: Wrench,
      meta: 'All tools',
    },
    {
      id: 'in_use',
      label: 'In use',
      value: stats.inUse,
      accent: '#10b981',
      icon: Settings2,
      meta: `${stats.inUsePct}%`,
    },
    {
      id: 'available',
      label: 'Available',
      value: stats.available,
      accent: '#0ea5e9',
      icon: CheckCircle2,
      meta: `${stats.availablePct}%`,
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      value: stats.maintenance,
      accent: '#f59e0b',
      icon: AlertTriangle,
      meta: `${stats.maintenancePct}%`,
    },
  ];

  return (
    <div className="ti-page">
      <div className="ti-scroll">
        <div className="ti-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Tools inventory' },
            ]}
          />

          {loading && tools.length === 0 && !error ? (
            <TiPageSkeleton />
          ) : (
            <>
              <section className="ti-intro" aria-label="Tools inventory overview">
                <div className="ti-intro-text">
                  <h1 className="ti-intro-title">Tools inventory</h1>
                  <p className="ti-intro-subtitle">
                    Track testing tools, assignments, serial numbers, and estimated values
                  </p>
                </div>
                <div className="ti-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ti-soft-btn h-9 w-9"
                    onClick={handleExportToExcel}
                    disabled={tools.length === 0}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!isReadOnly ? (
                  <Button type="button" className="ti-create-btn" onClick={openCreateModal}>
                    <Plus className="h-4 w-4" />
                    Record tool
                  </Button>
                  ) : null}
                </div>
              </section>

              {error ? (
                <div className="ti-error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="ti-lot-tabs" role="tablist" aria-label="Filter by LOT">
                {LOT_TAB_OPTIONS.filter(
                  (option) => !lockedLotFilter || option.value === lockedLotFilter
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={lotFilter === option.value}
                    className={`ti-lot-tabs__btn${lotFilter === option.value ? ' is-active' : ''}`}
                    onClick={() => {
                      if (!lockedLotFilter) setLotFilter(option.value);
                    }}
                  >
                    {option.label}
                    <span className="ti-lot-tabs__count">{lotCounts[option.value] || 0}</span>
                  </button>
                ))}
              </div>

              <div className="ti-summary-cards">
                {summaryCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.id}
                      className="ti-summary-card"
                      style={{ '--cat-accent': card.accent }}
                    >
                      <div className="ti-summary-card__top">
                        <span className="ti-summary-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="ti-summary-card__label">{card.label}</span>
                      </div>
                      <div className="ti-summary-card__count">{card.meta}</div>
                      <div className="ti-summary-card__amount">
                        {card.value.toLocaleString('en-US')}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="ti-table-section__head">
                <h2>
                  Tools assignment
                  <span className="ti-table-section__count">{totalFiltered}</span>
                </h2>
                {totalFiltered > 0 ? (
                  <div className="ti-subtotal">
                    Subtotal: <strong>{subtotalValue}</strong>
                  </div>
                ) : null}
              </div>

              <div className="ti-toolbar">
                <div className="ti-search">
                  <Search className="ti-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="ti-search__input"
                    placeholder="Search tool, model, serial, user…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search tools"
                  />
                </div>
                <select
                  className="ti-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Status filter"
                >
                  <option value="all">All status</option>
                  <option value="in_use">In use</option>
                  <option value="available">Available</option>
                  <option value="maintenance">Maintenance</option>
                </select>
                <Input
                  className="ti-date-input"
                  placeholder="Start dd/mm/yyyy"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="Start date"
                />
                <Input
                  className="ti-date-input"
                  placeholder="End dd/mm/yyyy"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="End date"
                />
                <div className="ti-toolbar__spacer" aria-hidden="true" />
                <Button
                  type="button"
                  variant="ghost"
                  className="ti-soft-btn h-9 gap-1.5"
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Clear
                </Button>
              </div>

              <div className="ti-table-section">
                <div className="ti-table-shell">
                  <div className="ti-table-wrapper">
                    <table className="ti-table">
                      <thead>
                        <tr>
                          <th>LOT</th>
                          <th>Tool name</th>
                          <th>Model</th>
                          <th>Serial number</th>
                          <th>User</th>
                          <th>Date assigned</th>
                          <th>Estimated value</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTools.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="ti-empty-row">
                              {loading
                                ? 'Loading tools…'
                                : 'No tools found. Click “Record tool” to add one.'}
                            </td>
                          </tr>
                        ) : (
                          paginatedTools.map((tool) => (
                            <tr key={tool.id}>
                              <td>
                                <span
                                  className={`ti-lot-badge ${
                                    String(tool.lot || '').includes('LOT 8')
                                      ? 'lot-badge-8'
                                      : String(tool.lot || '').includes('LOT 10')
                                        ? 'lot-badge-10'
                                        : 'lot-badge-default'
                                  }`}
                                >
                                  {tool.lot || '—'}
                                </span>
                              </td>
                              <td className="ti-tool-name">{tool.name || '—'}</td>
                              <td>{tool.model || '—'}</td>
                              <td className="ti-code">{tool.serialNumber || '—'}</td>
                              <td>{tool.user || '—'}</td>
                              <td>{tool.dateAssigned || '—'}</td>
                              <td className="ti-value">{tool.estimatedValue || '—'}</td>
                              <td>
                                <span className={`ti-status-badge ti-status-${tool.status}`}>
                                  {statusLabel(tool.status)}
                                </span>
                              </td>
                              <td>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="ti-soft-btn h-8 gap-1.5"
                                  onClick={() => handleEditClick(tool)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {totalFiltered > 0 ? (
                  <div className="ti-pagination">
                    <div className="ti-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                    </div>
                    <div className="ti-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ti-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="ti-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="ti-pagination__ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`ti-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ti-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {showModal ? (
        <div className="ti-modal-overlay" onClick={handleCancel} role="presentation">
          <div
            className="ti-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ti-modal-title"
          >
            <div className="ti-modal__header">
              <h2 id="ti-modal-title">{editingToolId ? 'Edit tool' : 'Record tool'}</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ti-soft-btn h-8 w-8"
                onClick={handleCancel}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form className="ti-modal__form" onSubmit={handleRecordTool}>
              {formError ? (
                <div className="ti-modal__error" role="alert">
                  {formError}
                </div>
              ) : null}

              <div className="ti-field-row">
                <div className="ti-field">
                  <Label htmlFor="lot">LOT</Label>
                  <select
                    id="lot"
                    name="lot"
                    className="ti-modal-select"
                    value={formData.lot}
                    onChange={handleInputChange}
                  >
                    <option value="">Select LOT</option>
                    <option value="LOT 8 (Morogoro)">LOT 8 (Morogoro)</option>
                    <option value="LOT 10 (Manyara)">LOT 10 (Manyara)</option>
                  </select>
                </div>
                <div className="ti-field">
                  <Label htmlFor="name">Tool name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Earth tester"
                    required
                  />
                </div>
              </div>

              <div className="ti-field">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="e.g. KYORITSU 4105A"
                />
              </div>

              <div className="ti-field-row">
                <div className="ti-field">
                  <Label htmlFor="serialNumber">Serial number</Label>
                  <Input
                    id="serialNumber"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleInputChange}
                    placeholder="e.g. ET-4105A-00123"
                  />
                </div>
                <div className="ti-field">
                  <Label htmlFor="user">User name</Label>
                  <select
                    id="user"
                    name="user"
                    className="ti-modal-select"
                    value={formData.user}
                    onChange={handleInputChange}
                  >
                    <option value="">Select user</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.name}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ti-field-row">
                <div className="ti-field">
                  <Label htmlFor="dateAssigned">Date assigned</Label>
                  <Input
                    id="dateAssigned"
                    name="dateAssigned"
                    value={formData.dateAssigned}
                    onChange={handleInputChange}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                <div className="ti-field">
                  <Label htmlFor="estimatedValue">Estimated value</Label>
                  <Input
                    id="estimatedValue"
                    name="estimatedValue"
                    value={formData.estimatedValue}
                    onChange={handleInputChange}
                    placeholder="e.g. TZS 8,500,000"
                  />
                </div>
              </div>

              <div className="ti-field">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  name="status"
                  className="ti-modal-select"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="in_use">In use</option>
                  <option value="available">Available</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div className="ti-modal__actions">
                <Button
                  type="button"
                  variant="ghost"
                  className="ti-soft-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingToolId ? 'Save changes' : 'Save tool'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ToolsInventory;
